import React from 'react';
import { useAuth } from '../auth/AuthContext';

export type ViewMode = 'home' | 'app' | 'dev-test';

interface AppTopBarProps {
    viewMode: ViewMode;
    onViewModeChange: (v: ViewMode) => void;
    onWordmarkClick: () => void;
    onMyAccount: () => void;
    onOpenAuth: (tab: 'login' | 'register') => void;
    showDevTab: boolean;
    onToggleDebug?: () => void;
    showDebug?: boolean;
    /** When the signed-in user's email is unverified, surface a subtle inline verify link in the account block. */
    unverified?: boolean;
    onResendVerification?: () => void;
    resendState?: 'idle' | 'sending' | 'sent';
}

/**
 * Chambers app top bar — two-row editorial chrome.
 *
 *  ┌──────────────────────────────────────────────────────┐
 *  │ Estateclarity · PROBATE & IHT REVIEW    email · sign out │  52px
 *  ├──────────────────────────────────────────────────────┤
 *  │ I. Estate assessment   II. Test estates       [DEBUG]│  48px
 *  └──────────────────────────────────────────────────────┘
 *
 * Total height: 100px.
 */
export function AppTopBar({
    viewMode,
    onViewModeChange,
    onWordmarkClick,
    onMyAccount,
    onOpenAuth,
    showDevTab,
    onToggleDebug,
    showDebug,
    unverified,
    onResendVerification,
    resendState,
}: AppTopBarProps) {
    const { user, logout, isLoading } = useAuth();

    const initial = (user?.email ?? '?').charAt(0).toUpperCase();

    return (
        <header className="bg-white sticky top-0 z-40">
            {/* Top strip — wordmark + account */}
            <div className="flex items-center justify-between border-b border-rule-soft px-8 h-[52px]">
                <button
                    onClick={onWordmarkClick}
                    className="flex items-baseline gap-3.5 hover:opacity-80 transition-opacity"
                    aria-label="Home"
                >
                    <span className="font-serif text-[18px] text-ink leading-none">
                        Estate<span className="italic">clarity</span>
                    </span>
                    <span className="text-[9px] tracking-eyebrow uppercase text-ink3 border-l border-rule pl-3.5 hidden md:inline">
                        Probate &amp; IHT review
                    </span>
                </button>

                <div className="flex items-center gap-[18px]">
                    {isLoading ? null : user ? (
                        <>
                            <div className="flex items-center gap-2.5">
                                <span
                                    aria-hidden
                                    className="w-7 h-7 rounded-full border border-rule bg-white flex items-center justify-center font-serif text-[12px] text-accent leading-none"
                                >
                                    {initial}
                                </span>
                                <span className="text-[11px] text-ink3 hidden sm:block">
                                    {user.email}
                                </span>
                            </div>
                            {unverified && onResendVerification && (
                                <>
                                    <div className="w-px h-3.5 bg-rule hidden sm:block" />
                                    {resendState === 'sent' ? (
                                        <span className="text-[11px] text-[#7A5A00] hidden sm:block">
                                            Verification sent
                                        </span>
                                    ) : (
                                        <button
                                            onClick={onResendVerification}
                                            disabled={resendState === 'sending'}
                                            title="Resend verification email"
                                            className="text-[11px] text-[#7A5A00] hover:text-ink underline decoration-dotted underline-offset-4 disabled:opacity-50 hidden sm:block"
                                        >
                                            {resendState === 'sending' ? 'Sending…' : 'Verify email'}
                                        </button>
                                    )}
                                </>
                            )}
                            <div className="w-px h-3.5 bg-rule" />
                            <button
                                onClick={onMyAccount}
                                className="text-[11px] text-ink3 hover:text-ink transition-colors"
                            >
                                Account
                            </button>
                            <div className="w-px h-3.5 bg-rule" />
                            <button
                                onClick={logout}
                                className="text-[11px] text-ink3 hover:text-ink transition-colors"
                            >
                                Sign out
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => onOpenAuth('login')}
                                className="text-[11px] text-ink3 hover:text-ink transition-colors"
                            >
                                Sign in
                            </button>
                            <button
                                onClick={() => onOpenAuth('register')}
                                className="text-[11px] font-medium text-accent hover:text-accent-hover transition-colors"
                            >
                                Create account
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Bottom strip — numeral tabs */}
            <div className="flex items-center gap-7 border-b border-rule px-8 h-12 overflow-x-auto">
                <NumeralTab
                    numeral="I"
                    label="Estate assessment"
                    active={viewMode === 'app'}
                    onClick={() => onViewModeChange('app')}
                />
                {showDevTab && (
                    <NumeralTab
                        numeral="II"
                        label="Test estates"
                        active={viewMode === 'dev-test'}
                        onClick={() => onViewModeChange('dev-test')}
                    />
                )}
                <div className="flex-1" />
                {onToggleDebug && (
                    <button
                        onClick={onToggleDebug}
                        className={`font-mono text-[10px] tracking-[0.14em] px-2.5 py-1 border transition-colors whitespace-nowrap ${
                            showDebug
                                ? 'border-accent text-accent bg-accent-soft'
                                : 'border-rule text-ink3 hover:text-ink hover:border-ink3'
                        }`}
                    >
                        DEBUG
                    </button>
                )}
            </div>
        </header>
    );
}

interface NumeralTabProps {
    numeral: string;
    label: string;
    active: boolean;
    onClick: () => void;
}

function NumeralTab({ numeral, label, active, onClick }: NumeralTabProps) {
    return (
        <button
            onClick={onClick}
            className={`h-full flex items-center gap-2.5 px-1.5 -mb-px border-b-2 transition-colors whitespace-nowrap ${
                active ? 'border-accent' : 'border-transparent'
            }`}
            aria-current={active ? 'page' : undefined}
        >
            <span
                className={`font-serif italic text-[14px] ${
                    active ? 'text-accent' : 'text-sub'
                }`}
            >
                {numeral}.
            </span>
            <span
                className={`text-[13px] tracking-[0.01em] ${
                    active
                        ? 'text-ink font-semibold'
                        : 'text-ink2 font-normal'
                }`}
            >
                {label}
            </span>
        </button>
    );
}
